import type { EngineOverview } from '@/shared/services/declarationsNew.service';
import { splitPeriod } from '../lib/periods';
import { DECLARABLE_BY_CODE } from '../lib/regimes';
import type { DeclarationsSummary, SummaryAnnual, SummaryCell, SummaryMonth } from './types';

const OBLIGATED_ANNUAL_STATES = ['pendiente', 'presentada', 'por_pagar'];

const PRESENTED_ANNUAL_STATES = ['presentada', 'por_pagar'];

const regimeName = (code: string) => DECLARABLE_BY_CODE[code as keyof typeof DECLARABLE_BY_CODE] ?? code;

const blankCell = (regime: string, income = 0, status = ''): SummaryCell => ({
	regime,
	income,
	hadRegime: undefined,
	daysLate: 0,
	status,
	pending: false,
	sellable: false,
	inProgress: false,
});

const rowKey = (year: number, month: number) => `${year}-${month}`;

const monthsOf = (overview: EngineOverview | undefined): SummaryMonth[] => {
	const rows = new Map<string, SummaryMonth>();
	const rowOf = (period: string) => {
		const { year, month } = splitPeriod(period);
		const key = rowKey(year, month);
		const existing = rows.get(key);
		if (existing) return existing;
		const created: SummaryMonth = { year, month, regimes: [], cells: [] };
		rows.set(key, created);
		return created;
	};

	for (const year of overview?.years ?? []) {
		for (const month of year.months) {
			const row = rowOf(month.period);
			row.regimes = month.regimes.map(regimeName);
			const cells = month.cells ?? [];
			row.cells =
				cells.length > 0
					? cells.map((cell) =>
							blankCell(regimeName(cell.regime), cell.income ?? (cells.length === 1 ? (month.income ?? 0) : 0), cell.state),
						)
					: [];
		}
	}

	for (const item of overview?.catchUp ?? []) {
		const row = rowOf(item.period);
		const regime = regimeName(item.regime);
		let cell = row.cells.find((candidate) => candidate.regime === regime);
		if (!cell) {
			cell = blankCell(regime);
			row.cells.push(cell);
		}
		cell.income = item.income ?? cell.income;
		cell.hadRegime = item.hadRegime;
		cell.daysLate = item.daysLate;
		cell.status = item.state;
		cell.pending = item.sellable;
		cell.sellable = item.sellable;
		cell.inProgress = !item.sellable;
	}

	return [...rows.values()].sort((a, b) => a.year - b.year || a.month - b.month);
};

const annualsOf = (overview: EngineOverview | undefined): SummaryAnnual[] =>
	(overview?.years ?? []).flatMap((year) => {
		const annual = year.annual;
		if (!annual) return [];
		const purchased = annual.purchased ?? false;
		const open = annual.state === 'pendiente';
		return [
			{
				year: annual.year,
				income: annual.income,
				daysLate: annual.daysLate ?? 0,
				status: annual.state,
				obligated: OBLIGATED_ANNUAL_STATES.includes(annual.state),
				open,
				sellable: annual.sellable ?? false,
				purchased,
				settled: purchased || PRESENTED_ANNUAL_STATES.includes(annual.state),
				inProgress: purchased && open,
			},
		];
	});

export type NewSummaryInput = {
	overview: EngineOverview | undefined;
	failed?: boolean;
};

export const summaryFromNew = ({ overview, failed = false }: NewSummaryInput): DeclarationsSummary => ({
	source: 'new',
	loading: !failed && !overview,
	reliable: overview?.catchUpSync === 'listo',
	months: monthsOf(overview),
	annuals: annualsOf(overview),
});
